import { useNavigate, useRouterState } from '@tanstack/react-router'
import { useQueryClient } from '@tanstack/react-query'
import {
  ArrowLeftRight,
  BanknoteIcon,
  BarChart3,
  Boxes,
  Church,
  ClipboardList,
  Cog,
  FileBadge,
  FileClock,
  FolderSync,
  Folders,
  HandCoins,
  Home,
  Mail,
  Package,
  UserRoundCog,
  Users,
  Wallet,
} from 'lucide-react'
import type { JSX } from 'react'
import type { UserRole } from '@/features/users/types/user'
import { CHURCHES } from '@/features/churches/constants/endpoints'
import { DONATIONS } from '@/features/donations/constants/endpoints'
import { EXPENSES } from '@/features/expenses/constants/endpoints'
import {
  INVENTORY,
  STOCK,
  TRANSACTIONS,
} from '@/features/inventory/constants/endpoints'
import { MEMBERS } from '@/features/members/constants/endpoints'
import { USERS } from '@/features/users/constants/endpoints'
import { useAuthStore } from '@/features/auth/store/authStore'
import { PermissionHelper, usePermissions } from '@/utils/permissions'

export interface NavItem {
  to: string
  label: string
  icon: JSX.Element
}

export interface NavGroup {
  id: 'donations' | 'admin' | 'inventory' | 'expenses'
  label: string
  icon: JSX.Element
  items: Array<NavItem>
}

const homeItem: NavItem = {
  to: '/',
  label: 'Home',
  icon: <Home size={20} />,
}

const donationsItems: Array<NavItem> = [
  { to: '/donations', label: 'Donations', icon: <HandCoins size={20} /> },
  {
    to: '/registrations',
    label: 'Registrations',
    icon: <ClipboardList size={20} />,
  },
  {
    to: '/annual-summary',
    label: 'Annual Summary',
    icon: <FileBadge size={20} />,
  },
  { to: '/reports', label: 'Reports', icon: <BarChart3 size={20} /> },
]

const adminItems: Array<NavItem> = [
  { to: '/churches', label: 'Churches', icon: <Church size={20} /> },
  { to: '/members', label: 'Members', icon: <Users size={20} /> },
  { to: '/users', label: 'Users', icon: <Users size={20} /> },
]

const inventoryItems: Array<NavItem> = [
  { to: '/inventory', label: 'Inventory', icon: <Boxes size={20} /> },
  {
    to: '/inventory/transactions',
    label: 'Transactions',
    icon: <ArrowLeftRight size={20} />,
  },
  {
    to: '/inventory/categories',
    label: 'Categories',
    icon: <Folders size={20} />,
  },
]

const expensesItems: Array<NavItem> = [
  {
    to: '/expenses',
    label: 'Expenses',
    icon: <BanknoteIcon size={20} />,
  },
  {
    to: '/expenses/categories',
    label: 'Categories',
    icon: <Folders size={20} />,
  },
]

const settingsItems: Array<NavItem> = [
  {
    to: '/email-dashboard',
    label: 'Email Settings',
    icon: <Mail size={20} />,
  },
  {
    to: '/donation-automation',
    label: 'Donation Automation',
    icon: <Cog size={20} />,
  },
  {
    to: '/profile',
    label: 'User Profile',
    icon: <UserRoundCog size={20} />,
  },
  {
    to: '/audit',
    label: 'Audit Logs',
    icon: <FileClock size={20} />,
  },
  {
    to: '/backup',
    label: 'Backup',
    icon: <FolderSync size={20} />,
  },
]

export function useNavigationBar() {
  const { location } = useRouterState()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const user = useAuthStore((state) => state.user)
  const activeRole = useAuthStore((state) => state.activeRole)
  const setActiveRole = useAuthStore((state) => state.setActiveRole)
  const logout = useAuthStore((state) => state.logout)
  const permissions = usePermissions()

  // Check if a role has access to the current page
  const hasRoleAccessToCurrentPage = (roleId: UserRole): boolean => {
    const rolePermissions = new PermissionHelper({ user, activeRole: roleId })

    const paths: Record<string, boolean> = {
      '/': rolePermissions.canAccessHome(),
      '/churches': rolePermissions.canAccessChurches(),
      '/members': rolePermissions.canAccessMembers(),
      '/donations': rolePermissions.canAccessDonations(),
      '/registrations': rolePermissions.canAccessDonations(),
      '/users': rolePermissions.canAccessUsers(),
      '/reports': rolePermissions.canAccessReports(),
      '/annual-summary': rolePermissions.canAccessAnnualSummary(),
      '/inventory': rolePermissions.canAccessInventory(),
      '/expenses': rolePermissions.canAccessExpenses(),
      '/email-dashboard': rolePermissions.canAccessEmailSettings(),
      '/donation-automation': rolePermissions.canAccessDonationAutomation(),
      '/profile': rolePermissions.canAccessUserProfile(),
      '/audit': rolePermissions.canAccessAuditLog(),
      '/backup': rolePermissions.canAccessBackup(),
    }

    return paths[location.pathname] ?? true
  }

  const getAllowedInitializeQueryRoots = (roleId: UserRole): Set<string> => {
    const rolePermissions = new PermissionHelper({ user, activeRole: roleId })
    const allowedRoots = new Set<string>([USERS, CHURCHES, MEMBERS])

    if (rolePermissions.canAccessDonations()) {
      allowedRoots.add(DONATIONS)
    }

    if (rolePermissions.canAccessInventory()) {
      allowedRoots.add(STOCK)
      allowedRoots.add(TRANSACTIONS)
      allowedRoots.add(INVENTORY)
    }

    if (rolePermissions.canAccessExpenses()) {
      allowedRoots.add(EXPENSES)
    }

    return allowedRoots
  }

  // Handle role switching with access checking
  const handleRoleChange = (roleId: UserRole) => {
    if (roleId === activeRole) return

    const allowedRoots = getAllowedInitializeQueryRoots(roleId)
    const shouldRefreshQuery = (queryKey: ReadonlyArray<unknown>) => {
      const firstKey = queryKey[0]
      if (typeof firstKey !== 'string') return false
      return allowedRoots.has(firstKey)
    }

    setActiveRole(roleId)

    // Refresh only role-allowed initialization queries to avoid 403s.
    void Promise.all([
      queryClient.invalidateQueries({
        predicate: (query) => shouldRefreshQuery(query.queryKey),
        refetchType: 'none',
      }),
      queryClient.refetchQueries({
        type: 'active',
        predicate: (query) => shouldRefreshQuery(query.queryKey),
      }),
    ])

    if (!hasRoleAccessToCurrentPage(roleId)) {
      navigate({ to: '/' })
    }
  }

  // Filter items by permissions
  const filterItemsByPermission = (
    items: Array<NavItem>,
    permissionMap: Record<string, boolean>,
  ): Array<NavItem> => {
    return items.filter((item) => permissionMap[item.to] ?? true)
  }

  // Get visible navigation items
  const getVisibleHomeItem = (): NavItem | null => {
    return permissions.canAccessHome() ? homeItem : null
  }

  const getVisibleDonationsItems = (): Array<NavItem> => {
    return filterItemsByPermission(donationsItems, {
      '/donations': permissions.canAccessDonations(),
      '/registrations': permissions.canAccessDonations(),
      '/annual-summary': permissions.canAccessAnnualSummary(),
      '/reports': permissions.canAccessReports(),
    })
  }

  const getVisibleAdminItems = (): Array<NavItem> => {
    return filterItemsByPermission(adminItems, {
      '/churches': permissions.canAccessChurches(),
      '/members': permissions.canAccessMembers(),
      '/users': permissions.canAccessUsers(),
    })
  }

  const getVisibleInventoryItems = (): Array<NavItem> => {
    return filterItemsByPermission(inventoryItems, {
      '/inventory': permissions.canAccessInventory(),
      '/inventory/transactions': permissions.canAccessInventory(),
      '/inventory/categories': permissions.canAccessInventory(),
    })
  }

  const getVisibleExpensesItems = (): Array<NavItem> => {
    return filterItemsByPermission(expensesItems, {
      '/expenses': permissions.canAccessExpenses(),
      '/expenses/categories': permissions.isSystemAdmin(),
    })
  }

  const getVisibleSettingsItems = (): Array<NavItem> => {
    return filterItemsByPermission(settingsItems, {
      '/email-dashboard': permissions.canAccessEmailSettings(),
      '/donation-automation': permissions.canAccessDonationAutomation(),
      '/profile': permissions.canAccessUserProfile(),
      '/audit': permissions.canAccessAuditLog(),
      '/backup': permissions.canAccessBackup(),
    })
  }

  // Get navigation groups
  const getNavigationGroups = (): Array<NavGroup> => {
    const groups: Array<NavGroup> = []

    const visibleDonations = getVisibleDonationsItems()
    if (visibleDonations.length > 0) {
      groups.push({
        id: 'donations',
        label: 'Donations',
        icon: <HandCoins size={20} />,
        items: visibleDonations,
      })
    }

    const visibleInventory = getVisibleInventoryItems()
    if (visibleInventory.length > 0) {
      groups.push({
        id: 'inventory',
        label: 'Inventory',
        icon: <Package size={20} />,
        items: visibleInventory,
      })
    }

    const visibleExpenses = getVisibleExpensesItems()
    if (visibleExpenses.length > 0) {
      groups.push({
        id: 'expenses',
        label: 'Expenses',
        icon: <Wallet size={20} />,
        items: visibleExpenses,
      })
    }

    const visibleAdmin = getVisibleAdminItems()
    if (visibleAdmin.length > 0) {
      groups.push({
        id: 'admin',
        label: 'Administrative',
        icon: <Folders size={20} />,
        items: visibleAdmin,
      })
    }

    return groups
  }

  // Derive expanded group from current path
  const deriveGroupFromPath = ():
    | 'donations'
    | 'admin'
    | 'inventory'
    | 'expenses'
    | null => {
    const p = location.pathname
    if (
      p.startsWith('/donations') ||
      p === '/reports' ||
      p === '/annual-summary' ||
      p === '/registrations'
    )
      return 'donations'
    if (
      p.startsWith('/churches') ||
      p.startsWith('/members') ||
      p.startsWith('/users')
    )
      return 'admin'
    if (p.startsWith('/inventory')) return 'inventory'
    if (p.startsWith('/expenses')) return 'expenses'
    return null
  }

  // Check if path is active (exact match only)
  const isPathActive = (path: string): boolean => {
    return location.pathname === path
  }

  return {
    location,
    navigate,
    user,
    activeRole,
    logout,
    permissions,
    handleRoleChange,
    hasRoleAccessToCurrentPage,
    getVisibleHomeItem,
    getVisibleDonationsItems,
    getVisibleAdminItems,
    getVisibleInventoryItems,
    getVisibleExpensesItems,
    getVisibleSettingsItems,
    getNavigationGroups,
    deriveGroupFromPath,
    isPathActive,
  }
}
